/**
 * Helpers de sessão.
 *
 * Os guards de rota e os repositories precisam saber, de forma síncrona, se
 * existe uma sessão válida. Só olhar se a chave `access_token` existe no
 * localStorage não basta: um token expirado (ou lixo deixado por uma sessão
 * antiga) continua "existindo" e liberava o acesso às telas internas.
 */

const TOKEN_KEY = "access_token";
const UNIDADE_KEY = "selected_unidade_id";
const EMPRESA_KEY = "selected_empresa_id";

const SESSION_KEYS = [TOKEN_KEY, UNIDADE_KEY, EMPRESA_KEY];

export function getAccessToken() {
	const token = localStorage.getItem(TOKEN_KEY);
	// Strings "undefined"/"null" acontecem quando um login mal sucedido grava
	// um valor indefinido; tratamos como ausência de token.
	if (!token || token === "undefined" || token === "null") return null;
	return token;
}

function decodeTokenPayload(token) {
	try {
		const payload = token.split(".")[1];
		if (!payload) return null;

		const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
		const json = decodeURIComponent(
			atob(base64)
				.split("")
				.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
				.join("")
		);

		return JSON.parse(json);
	} catch (error) {
		return null;
	}
}

/**
 * Sessão válida = token presente, decodificável e ainda dentro da validade.
 * Qualquer coisa fora disso é tratada como não autenticado.
 */
export function isSessionValid() {
	const token = getAccessToken();
	if (!token) return false;

	const payload = decodeTokenPayload(token);
	if (!payload || typeof payload.exp !== "number") return false;

	return payload.exp * 1000 > Date.now();
}

export function hasUnidadeSelected() {
	return !!localStorage.getItem(UNIDADE_KEY);
}

export function clearSession() {
	SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
}

/**
 * Alinha a unidade guardada no localStorage com o perfil recém-carregado.
 *
 * As telas leem `selected_unidade_id` direto do localStorage durante a
 * renderização, antes de qualquer efeito. Sem esta conferência, um id deixado
 * por outra sessão (login em sequência no mesmo navegador, ou vínculo que
 * deixou de existir) seguia sendo enviado à API — que responde 403 "Registro
 * pertence a outra empresa distribuidora" em toda tela, sem pista do motivo.
 *
 * Roda antes de o perfil entrar no contexto, então quando as telas renderizam
 * o valor já é de uma unidade que o usuário realmente tem.
 */
export function reconcileUnidadeSelecionada(profile) {
	const unidades = (profile?.unidadesDistribuidoras || [])
		.map((vinculo) => vinculo.unidadeDistribuidora)
		.filter(Boolean);

	if (unidades.length === 0) {
		// Sem unidade nenhuma não há o que selecionar, e manter a anterior só
		// produziria 403. Quem cuida do "para onde ir" é o guard de rota.
		localStorage.removeItem(UNIDADE_KEY);
		localStorage.removeItem(EMPRESA_KEY);
		return null;
	}

	const selecionadaId = localStorage.getItem(UNIDADE_KEY);
	const unidade =
		unidades.find((u) => String(u.id) === String(selecionadaId)) || unidades[0];

	localStorage.setItem(UNIDADE_KEY, String(unidade.id));
	if (unidade.empresaDistribuidorId) {
		localStorage.setItem(EMPRESA_KEY, String(unidade.empresaDistribuidorId));
	} else {
		localStorage.removeItem(EMPRESA_KEY);
	}

	return unidade;
}

/**
 * Encerra a sessão, limpando o localStorage.
 *
 * Continua devolvendo uma Promise: os chamadores encadeiam
 * `endSession().finally(redirectToLogin)` e, quando o suporte offline voltar,
 * a limpeza assíncrona do cache entra aqui sem mexer em quem chama.
 */
export function endSession() {
	clearSession();
	return Promise.resolve();
}

export function redirectToLogin() {
	if (window.location.pathname !== "/") {
		window.location.replace("/");
	}
}
